import React from 'react';
import PrototypeShell from './PrototypeShell';
import {
  ChartLineUp,
  UsersThree,
  ClipboardText,
  Target,
  Buildings,
  Download,
  Info,
  TrendUp,
  TrendDown,
  ShieldCheck,
} from '@phosphor-icons/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts';

const kpis = [
  {
    label: 'Active participants',
    value: '148',
    sub: 'across 3 cohorts',
    trend: 12,
    icon: UsersThree,
    testId: 'kpi-active-participants',
  },
  {
    label: 'Assessment completion',
    value: '87%',
    sub: 'of enrolled participants',
    trend: 4,
    icon: ClipboardText,
    testId: 'kpi-assessment-completion',
  },
  {
    label: 'Median readiness score',
    value: '68',
    sub: 'baseline → 74 at follow-up',
    trend: 9,
    icon: Target,
    testId: 'kpi-readiness-score',
  },
  {
    label: 'Weekly engagement',
    value: '4.2×',
    sub: 'avg logins per participant',
    trend: -3,
    icon: ChartLineUp,
    testId: 'kpi-weekly-engagement',
  },
];

const cohortData = [
  { cohort: 'Winter \'25', baseline: 61, followup: 71 },
  { cohort: 'Fall \'25', baseline: 58, followup: 69 },
  { cohort: 'Spring \'26', baseline: 64, followup: 72 },
];

const engagementTrend = [
  { week: 'W1', logins: 3.1, actions: 1.8 },
  { week: 'W2', logins: 3.8, actions: 2.4 },
  { week: 'W3', logins: 4.2, actions: 3.1 },
  { week: 'W4', logins: 4.5, actions: 3.6 },
  { week: 'W5', logins: 4.4, actions: 3.5 },
  { week: 'W6', logins: 4.6, actions: 3.9 },
  { week: 'W7', logins: 4.2, actions: 3.7 },
  { week: 'W8', logins: 4.3, actions: 3.8 },
];

const outcomes = [
  { label: 'Participants who started an emergency fund', pct: 64 },
  { label: 'Participants who set a monthly budget', pct: 71 },
  { label: 'Participants reporting reduced money stress', pct: 58 },
  { label: 'Participants who completed ≥ 3 learning modules', pct: 82 },
];

const cohorts = [
  {
    name: 'Spring 2026 · Career Restart',
    size: 62,
    completion: 92,
    coach: 'Marcus Alvarez',
    status: 'On track',
    statusTone: 'success',
  },
  {
    name: 'Spring 2026 · Youth Employment',
    size: 44,
    completion: 78,
    coach: 'Priya Shah',
    status: 'Needs attention',
    statusTone: 'warning',
  },
  {
    name: 'Winter 2025 · Reentry',
    size: 42,
    completion: 95,
    coach: 'Devon Miller',
    status: 'Completed',
    statusTone: 'info',
  },
];

const toneMap = {
  success: 'bg-signal-success-soft text-signal-success',
  warning: 'bg-signal-warning-soft text-signal-warning',
  info: 'bg-signal-info-soft text-signal-info',
};

const WorkforceAdminOutcomes = () => {
  return (
    <PrototypeShell
      title="Program Administrator Outcomes Dashboard"
      subtitle="De-identified aggregate outcomes · grant-report ready · updated hourly"
    >
      {/* Privacy strip */}
      <div className="mb-8 flex items-start gap-3 bg-brand-navy/5 border border-brand-navy/10 rounded-xl px-4 py-3 text-sm">
        <ShieldCheck size={18} weight="duotone" className="text-brand-navy flex-shrink-0 mt-0.5" />
        <div className="text-ink-muted">
          <span className="font-semibold text-brand-navy">Privacy note:</span> Individual participant records
          are never shown on this screen. All values are aggregated with a minimum cohort size of 10; smaller
          groups are suppressed to prevent re-identification.
          <a href="#" className="underline text-brand-navy hover:text-brand-gold ml-1">
            See our aggregation policy
          </a>
        </div>
      </div>

      {/* Header controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Buildings size={16} weight="duotone" className="text-brand-navy" />
          <span className="font-semibold text-ink">Sample Workforce Partner</span>
          <span className="text-ink-soft hidden sm:inline">·</span>
          <select
            className="text-sm bg-white border border-surface-border rounded-lg px-3 py-1.5 text-ink font-medium focus:border-brand-gold focus:outline-none w-full sm:w-auto"
            data-testid="cohort-filter"
          >
            <option>All active cohorts (3)</option>
            <option>Spring 2026 · Career Restart</option>
            <option>Spring 2026 · Youth Employment</option>
            <option>Winter 2025 · Reentry</option>
          </select>
          <select
            className="text-sm bg-white border border-surface-border rounded-lg px-3 py-1.5 text-ink font-medium focus:border-brand-gold focus:outline-none w-full sm:w-auto"
            data-testid="timeframe-filter"
          >
            <option>Last 90 days</option>
            <option>Program to date</option>
            <option>Custom range…</option>
          </select>
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <button
            className="inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg border border-surface-border text-ink-muted hover:text-brand-navy hover:border-brand-navy transition w-full md:w-auto"
            data-testid="export-report-button"
          >
            <Download size={14} weight="bold" /> Export grant report
          </button>
        </div>
      </div>

      {/* KPI grid */}
      <section className="grid md:grid-cols-4 gap-4 mb-8">
        {kpis.map((k) => {
          const Icon = k.icon;
          const positive = k.trend >= 0;
          return (
            <div
              key={k.label}
              className="bg-white rounded-2xl border border-surface-border shadow-fluent-sm p-5"
              data-testid={k.testId}
            >
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-brand-navy/5 text-brand-navy flex items-center justify-center">
                  <Icon size={18} weight="duotone" />
                </div>
                <span
                  className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                    positive
                      ? 'bg-signal-success-soft text-signal-success'
                      : 'bg-signal-danger-soft text-signal-danger'
                  }`}
                >
                  {positive ? <TrendUp size={11} weight="bold" /> : <TrendDown size={11} weight="bold" />}
                  {Math.abs(k.trend)}%
                </span>
              </div>
              <p className="text-[11px] uppercase tracking-widest text-ink-soft font-semibold mt-3">
                {k.label}
              </p>
              <p className="font-heading text-3xl text-ink leading-tight mt-1">{k.value}</p>
              <p className="text-xs text-ink-soft mt-1">{k.sub}</p>
            </div>
          );
        })}
      </section>

      {/* Charts row */}
      <section className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-surface-border shadow-fluent-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ChartLineUp size={18} weight="duotone" className="text-brand-navy" />
              <h3 className="font-heading text-lg text-ink">Weekly engagement trend</h3>
            </div>
            <span className="text-[11px] uppercase tracking-widest text-ink-soft font-semibold">8 weeks</span>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={engagementTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="week" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="logins"
                stroke="#0A1128"
                strokeWidth={2.5}
                dot={{ r: 3 }}
                name="Avg logins / participant"
              />
              <Line
                type="monotone"
                dataKey="actions"
                stroke="#C5A059"
                strokeWidth={2.5}
                dot={{ r: 3 }}
                name="Avg actions / participant"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl border border-surface-border shadow-fluent-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Target size={18} weight="duotone" className="text-brand-navy" />
            <h3 className="font-heading text-lg text-ink">Readiness · baseline vs follow-up</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={cohortData} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" stroke="#64748B" fontSize={11} domain={[0, 100]} />
              <YAxis type="category" dataKey="cohort" stroke="#64748B" fontSize={11} width={80} />
              <Tooltip />
              <Legend />
              <Bar dataKey="baseline" fill="#94A3B8" name="Baseline" radius={[0, 4, 4, 0]} />
              <Bar dataKey="followup" fill="#C5A059" name="Follow-up" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Outcomes bars */}
      <section className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-2xl border border-surface-border shadow-fluent-sm p-6">
          <h3 className="font-heading text-lg text-ink mb-1">Reported outcomes</h3>
          <p className="text-xs text-ink-soft mb-5 inline-flex items-center gap-1">
            <Info size={11} /> Consented, participant-reported at follow-up assessment
          </p>
          <ul className="space-y-4" data-testid="outcomes-list">
            {outcomes.map((o) => (
              <li key={o.label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-ink-muted">{o.label}</span>
                  <span className="font-semibold text-brand-navy">{o.pct}%</span>
                </div>
                <div className="h-2 bg-surface-muted rounded-full overflow-hidden">
                  <div className="h-full bg-brand-gold" style={{ width: `${o.pct}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white rounded-2xl border border-surface-border shadow-fluent-sm p-6">
          <h3 className="font-heading text-lg text-ink mb-1">Cohorts</h3>
          <p className="text-xs text-ink-soft mb-5">Status is determined by engagement + completion signals</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cohorts-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-ink-soft font-semibold">
                  <th className="text-left pb-3">Cohort</th>
                  <th className="text-right pb-3">Size</th>
                  <th className="text-right pb-3">Completion</th>
                  <th className="text-right pb-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {cohorts.map((c) => (
                  <tr key={c.name} className="hover:bg-surface-subtle transition">
                    <td className="py-3">
                      <div className="font-semibold text-ink leading-tight">{c.name}</div>
                      <div className="text-[11px] text-ink-soft">Coach: {c.coach}</div>
                    </td>
                    <td className="py-3 text-right text-ink-muted">{c.size}</td>
                    <td className="py-3 text-right text-ink-muted">{c.completion}%</td>
                    <td className="py-3 text-right">
                      <span
                        className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full whitespace-nowrap ${
                          toneMap[c.statusTone]
                        }`}
                      >
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Compliance footer */}
      <section className="bg-brand-navy text-white rounded-2xl p-6 shadow-fluent-md">
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-brand-gold font-semibold mb-2">
              Data governance
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              Minimum-necessary aggregation. Individual-level detail is behind explicit participant consent
              (Decision 3: Metadata-only default).
            </p>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-brand-gold font-semibold mb-2">
              Report exports
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              Grant-ready formats: WIOA quarterly, CSBG, custom XLSX. Every export is signed with rule-engine
              version + consent-version metadata.
            </p>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-brand-gold font-semibold mb-2">
              Audit trail
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              Every metric on this page is traceable to source events and the rule-engine version that
              computed it — see Reports → Audit Log.
            </p>
          </div>
        </div>
      </section>
    </PrototypeShell>
  );
};

export default WorkforceAdminOutcomes;
